import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { PricingTerm } from '@/constants/pricingTypes';
import { QuoteState, QuoteAttribute } from '@/schemas/quoteResponseSchema';
import { ErrorResponse } from '@/schemas/errorResponseSchema';

interface QuoteStore {
  quote: QuoteState | null;
  showLoader: boolean;
  error: { data?: ErrorResponse; status?: number } | null;
  setError: (error: { data?: ErrorResponse; status?: number } | null) => void;
  setQuote: (quote: QuoteState) => void;
  setShowLoader: (showLoader: boolean) => void;
  updateQuoteAttributes: (
    symbol: string,
    scope: string,
    updater: (attrs: QuoteAttribute[]) => QuoteAttribute[]
  ) => void;
  updatePricingTerm: (term: PricingTerm) => void;
}

export const useQuoteStore = create<QuoteStore>()(
  devtools(
    (set) => ({
      quote: null,
      showLoader: false,
      error: null,

      setQuote: (quote) => set({ quote }, false, 'setQuote'),
      setShowLoader: (showLoader) => set({ showLoader }, false, 'setShowLoader'),
      setError: (error) => set({ error }, false, 'setError'),

      updateQuoteAttributes: (symbol, scope, updater) =>
        set(
          (state) => {
            if (!state.quote) return state;
            const coverageIndex = state.quote.coverages.findIndex(
              (coverage) => coverage.symbol === symbol && coverage.scope === scope
            );
            if (coverageIndex === -1) return state;

            return {
              quote: {
                ...state.quote,
                coverages: state.quote.coverages.map((coverage, index) =>
                  index === coverageIndex ? { ...coverage, attributes: updater(coverage.attributes) } : coverage
                ),
              },
            };
          },
          false,
          'updateQuoteAttributes'
        ),

      updatePricingTerm: (term) =>
        set(
          (state) => {
            if (!state.quote) return state;
            return {
              quote: {
                ...state.quote,
                details: { ...state.quote.details, initial_term_selection: term },
              },
            };
          },
          false,
          'updatePricingTerm'
        ),
    }),
    { name: 'QuoteStore' }
  )
);

// Selectors
export const useQuote = () => useQuoteStore((state) => state.quote);
export const useShowLoader = () => useQuoteStore((state) => state.showLoader);
export const useQuoteError = () => useQuoteStore((state) => state.error);
